import React, { useMemo, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import UserSettingsDialog from "@/components/UserSettingsDialog";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import {
  LayoutDashboard, Smartphone, Inbox, Bot, LogOut, MessageSquare, Image, Package,
  ShoppingCart, CreditCard, Zap, Terminal, Shield, Sparkles, Send, Menu, X, Settings,
  Sun, Moon,
} from "lucide-react";

const allNavItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", end: true, perm: "can_access_dashboard" },
  { to: "/dashboard/workspaces", icon: Smartphone, label: "Mis WhatsApps", perm: null },
  { to: "/dashboard/plans", icon: Sparkles, label: "Mi Plan", perm: null },
  { to: "/dashboard/connect-whatsapp", icon: Smartphone, label: "Connect WhatsApp", perm: "can_access_connect_whatsapp" },
  { to: "/dashboard/inbox", icon: Inbox, label: "Inbox", perm: "can_access_inbox" },
  { to: "/dashboard/broadcast", icon: Send, label: "Difusión masiva", perm: "can_access_broadcast" },
  { to: "/dashboard/catalog", icon: Package, label: "Catálogo", perm: "can_access_sales" },
  { to: "/dashboard/orders", icon: ShoppingCart, label: "Pedidos", perm: "can_access_sales" },
  { to: "/dashboard/payment-methods", icon: CreditCard, label: "Pagos", perm: "can_access_sales" },
  { to: "/dashboard/commands", icon: Terminal, label: "Comandos", perm: "can_access_commands" },
  { to: "/dashboard/keywords", icon: Zap, label: "Palabras Clave", perm: "can_access_keywords" },
  { to: "/dashboard/group-controls", icon: Shield, label: "Control Grupos", perm: "can_access_group_settings" },
  { to: "/dashboard/bot-studio", icon: Bot, label: "Bot Studio", perm: "can_access_bot_studio" },
  { to: "/dashboard/media-library", icon: Image, label: "Media Library", perm: "can_access_media_library" },
];

export default function DashboardLayout() {
  const { user, permissions, logout } = useAuth();
  const { branding } = useBranding();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const navItems = useMemo(() => {
    if (!permissions) return allNavItems;
    return allNavItems.filter((item) => item.perm === null || permissions[item.perm] !== false);
  }, [permissions]);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const pc = branding.primary_color || "#0f172a";
  const ac = branding.accent_color || "#22c55e";
  const bg = theme === "dark" ? "#0b1024" : (branding.secondary_color || "#f1f5f9");

  // Plan badge for the user button
  const expiresOn = user?.plan_expires_on;
  const daysLeft = expiresOn ? Math.ceil((new Date(expiresOn).getTime() - Date.now()) / 86400000) : null;
  const planBadge = daysLeft == null
    ? null
    : daysLeft < 0 ? { label: "Vencido", color: "bg-red-500" }
    : daysLeft <= 3 ? { label: `${daysLeft}d`, color: "bg-amber-500" }
    : null;

  const sidebarContent = (
    <>
      <div className="p-5 flex items-center gap-3">
        {branding.logo_url ? (
          <img src={branding.logo_url} alt="Logo" className="w-9 h-9 rounded-lg object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${ac}33` }}>
            <MessageSquare className="w-5 h-5" style={{ color: ac }} />
          </div>
        )}
        <span className="font-semibold text-base tracking-tight">{branding.sidebar_title || "WA Bot"}</span>
        <button onClick={() => setMobileOpen(false)} className="ml-auto md:hidden text-slate-400 hover:text-white" data-testid="sidebar-close">
          <X className="w-5 h-5" />
        </button>
      </div>
      <Separator className="bg-white/10" />
      <WorkspaceSwitcher />
      <nav className="flex-1 py-3 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive ? "bg-white/10 text-white font-medium" : "text-slate-400 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <item.icon className="w-4 h-4" />{item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <button
          onClick={() => { setSettingsOpen(true); setMobileOpen(false); }}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-white/5 transition"
          data-testid="user-menu-button"
        >
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold shrink-0" style={{ backgroundColor: ac, color: "#0b1024" }}>
            {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-400">Sesión iniciada</p>
            <p className="text-sm font-medium truncate text-white" data-testid="sidebar-user-email">{user?.name || user?.email}</p>
            {user?.active_plan_name && (
              <p className="text-xs text-slate-400 truncate flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-amber-400" /> {user.active_plan_name}
                {planBadge && <span className={`ml-1 text-[10px] text-white px-1.5 py-0.5 rounded-full ${planBadge.color}`}>{planBadge.label}</span>}
              </p>
            )}
          </div>
          <Settings className="w-4 h-4 text-slate-400 shrink-0" />
        </button>
        <div className="flex gap-1 mt-2">
          <Button variant="ghost" size="sm" onClick={toggle} className="flex-1 text-slate-400 hover:text-white hover:bg-white/5" data-testid="theme-toggle-sidebar">
            {theme === "dark" ? <Sun className="w-4 h-4 mr-1" /> : <Moon className="w-4 h-4 mr-1" />}
            {theme === "dark" ? "Claro" : "Oscuro"}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="flex-1 text-slate-400 hover:text-white hover:bg-white/5" data-testid="logout-button">
            <LogOut className="w-4 h-4 mr-1" /> Salir
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen relative" style={{ backgroundColor: bg }} data-testid="dashboard-layout">
      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center justify-between px-4 h-14 border-b" style={{ backgroundColor: pc, color: "white" }}>
        <button onClick={() => setMobileOpen(true)} className="text-white" data-testid="sidebar-open">
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt="" className="w-7 h-7 rounded-md object-cover" />
          ) : (
            <MessageSquare className="w-5 h-5" style={{ color: ac }} />
          )}
          <span className="font-semibold text-sm">{branding.sidebar_title || "WA Bot"}</span>
        </div>
        <button onClick={() => setSettingsOpen(true)} data-testid="user-menu-button-mobile">
          <Settings className="w-5 h-5 text-white" />
        </button>
      </header>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setMobileOpen(false)} aria-hidden />
      )}

      {/* Sidebar (drawer on mobile, fixed on desktop) */}
      <aside
        className={`fixed inset-y-0 left-0 w-64 text-white flex flex-col z-50 transform transition-transform md:relative md:translate-x-0 md:shrink-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ backgroundColor: pc }}
        data-testid="sidebar"
      >
        {sidebarContent}
      </aside>

      {/* Main content area */}
      <main
        className={`flex-1 overflow-y-auto pt-14 md:pt-0 ${theme === "dark" ? "text-slate-100" : ""}`}
        style={theme === "dark" ? { backgroundColor: "#0b1024" } : {}}
      >
        <Outlet />
      </main>

      <UserSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}
